// SkipActiveJobsPage — /skip/jobs/active
//
// Phase 6H — redefined to show ALL non-terminal jobs (status NOT IN
// 'collected', 'completed', 'cancelled'), not just jobs with a
// requested sub-flag. The page is now the primary operational view
// for in-progress work; the per-row "Actions ▾" dropdown surfaces
// the available actions for each job's current stage via the same
// useAvailableActions / actionKeyEndpoints plumbing the detail page
// uses.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card } from '../../../components/ui/Card/Card';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { useActiveActionJobs } from '../hooks/useSkipJobs';
import { JobRowActionsMenu } from './components/JobRowActionsMenu';
import { SkipJobStatusBadge } from './components/SkipJobStatusBadge';
import type { SkipJobSummary } from '../../../types/api';

import styles from './SkipActiveJobsPage.module.css';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function SkipActiveJobsPage(): JSX.Element {
  const [q, setQ] = useState('');
  // One dropdown open at a time at the page level (matches
  // SkipJobsListPage). null = no dropdown open.
  const [openActionsEncId, setOpenActionsEncId] = useState<string | null>(null);

  const query = useActiveActionJobs({ page: 1, limit: 100 });
  const allJobs = query.data?.jobs ?? [];

  const filteredJobs = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return allJobs;
    return allJobs.filter((j) => {
      return (
        j.job_number.toLowerCase().includes(term) ||
        j.customer_name.toLowerCase().includes(term) ||
        j.customer_postcode.toLowerCase().includes(term)
      );
    });
  }, [q, allJobs]);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Active Jobs"
        subtitle="All jobs currently in progress or out with clients"
      />

      <Card className={styles.filters}>
        <input
          type="search"
          placeholder="Search by job number, customer, or postcode…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={styles.search}
          aria-label="Search active jobs"
        />
      </Card>

      <Card>
        {query.isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : filteredJobs.length === 0 ? (
          <p className={styles.empty}>
            {q.trim() ? 'No active jobs match your search.' : 'No active jobs at this time.'}
          </p>
        ) : (
          <div className={styles.list}>
            {filteredJobs.map((j) => (
              <ActiveJobRow
                key={j.enc_id}
                job={j}
                isMenuOpen={openActionsEncId === j.enc_id}
                onToggleMenu={() =>
                  setOpenActionsEncId((prev) => (prev === j.enc_id ? null : j.enc_id))
                }
                onCloseMenu={() => setOpenActionsEncId(null)}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

interface ActiveJobRowProps {
  job: SkipJobSummary;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

function ActiveJobRow({
  job,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
}: ActiveJobRowProps): JSX.Element {
  const isPriority = job.is_priority === true;
  const collectionRequested = job.collection_status === 'requested';
  const exchangeRequested = job.exchange_status === 'requested';
  const tipReturnRequested = job.tip_return_status === 'requested';

  return (
    <div
      className={[styles.row, isPriority ? styles.priorityRow : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.rowMain}>
        <div className={styles.heading}>
          <Link
            to={`/skip/jobs/${encodeURIComponent(job.enc_id)}`}
            className={styles.jobNumber}
          >
            {job.job_number}
          </Link>
          <span className={styles.customer}>{job.customer_name}</span>
          {isPriority ? <span className={styles.priorityBadge}>Priority</span> : null}
          <SkipJobStatusBadge status={job.status} />
        </div>
        <div className={styles.meta}>
          <span>{job.job_type.replace(/_/g, ' ')}</span>
          {job.skip_number ? <span>· Unit {job.skip_number}</span> : null}
          {job.delivery_date ? <span>· {fmtDate(job.delivery_date)}</span> : null}
          <span>· {job.customer_postcode}</span>
        </div>
        {collectionRequested || exchangeRequested || tipReturnRequested ? (
          <div className={styles.badges}>
            {collectionRequested ? (
              <span className={styles.collectionBadge}>Collection Requested</span>
            ) : null}
            {exchangeRequested ? (
              <span className={styles.exchangeBadge}>Exchange Requested</span>
            ) : null}
            {tipReturnRequested ? (
              <span className={styles.tipReturnBadge}>Tip &amp; Return Requested</span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={styles.actions}>
        <JobRowActionsMenu
          jobEncId={job.enc_id}
          jobNumber={job.job_number}
          customerName={job.customer_name}
          skipSizeEncId={job.skip_size_enc_id}
          isOpen={isMenuOpen}
          onToggle={onToggleMenu}
          onClose={onCloseMenu}
        />
      </div>
    </div>
  );
}
